<template>
  <catalog-gradient-background :color="data?.tag.color">
    <app-page-wrapper class="mb-8 pt-6 md:mb-16 md:pt-12">
      <catalog-page-header v-if="data" :title="`Formations ${data.tag.label}`">
        <template #subtitle>
          <div
            v-if="data?.seo_content?.description"
            class="prose hidden text-subtle md:block"
            v-html="data.seo_content.description"
          />
        </template>
      </catalog-page-header>
      <catalog-page-header-placeholder v-else />
    </app-page-wrapper>

    <app-page-wrapper class="flex flex-col gap-16 md:gap-24">
      <div v-show="pending" class="grid grid-flow-col gap-4">
        <course-card-placeholder
          v-for="i in 10"
          :key="i"
          class="w-[250px]"
          skeleton-class="bg-primary-100"
        />
      </div>

      <course-card-grid id="catalog" :list="courses" />

      <category-page-teachers-list
        v-if="data && teachers.length > 0"
        :list="teachers"
        :view="data"
      />

      <catalog-missing-topic-contact-banner :catalog-page-id="slug" />

      <category-page-rating-list v-if="data" :view="data" />

      <category-pages-blog-post-list
        v-if="data"
        :courses="courses"
        :view="data"
      />

      <brand-customer-logos-banner />

      <brand-nps-banner>
        <template #append>
          <growth-contact-button
            color="dark"
            origin="catalog"
            origin_page_action="nps_banner_cta"
            :origin_page_id="slug"
            size="xl"
          />
        </template>
      </brand-nps-banner>

      <brand-qualiopi-banner />

      <div
        v-if="data?.seo_content?.content"
        class="prose m-auto w-full max-w-[900px]"
        v-html="data.seo_content.content"
      />
    </app-page-wrapper>
  </catalog-gradient-background>
</template>

<script lang="ts" setup>
import type { RouteLocationNormalized } from "#vue-router";
import {
  tagPageCacheKey,
  useTagPageView,
} from "~/business-areas/tag-page/composables/tag-page-view.hook";
import CatalogGradientBackground from "~/pages/catalog/_includes/components/catalog-gradient-background.vue";
import CatalogMissingTopicContactBanner from "~/pages/catalog/_includes/components/catalog-missing-topic-contact-banner.vue";
import CatalogPageHeader from "~/pages/catalog/_includes/components/catalog-page-header.vue";
import CatalogPageHeaderPlaceholder from "~/pages/catalog/_includes/components/catalog-page-header-placeholder.vue";
import CategoryPageRatingList from "~/pages/catalog/index/categories/_includes/components/category-page-rating-list.vue";
import CategoryPageTeachersList from "~/pages/catalog/index/categories/_includes/components/category-page-teachers-list.vue";
import CategoryPagesBlogPostList from "~/pages/catalog/index/categories/_includes/components/category-pages-blog-post-list.vue";

definePageMeta({
  async middleware(to) {
    const slug = (
      to as RouteLocationNormalized<"catalog-index-categories-slug">
    ).params.slug;

    const { data } = await useFetch(`/api/tag-pages/${slug}`, {
      key: tagPageCacheKey(slug),
    });

    if (!data.value)
      return navigateTo({
        name: "catalog-inter",
        query: {
          tags: slug,
        },
      });
  },
});

const slug = useRoute("catalog-index-categories-slug").params.slug;

const { data, courses, pending } = useTagPageView(slug);

const { data: teachers } = useTeacherList(`top_trainers_${slug}`, {
  tags: [slug],
});

useSeoMeta({
  title: () => data.value?.tag.label ?? "",
  description: () => data.value?.seo_content?.metadescription,
});
</script>
